import { productRoleSchema, inviteTypeSchema, PRODUCT_ROLE_PERMISSIONS } from '@dcp/shared'
import type { InviteType } from '@/hooks/useOrganizationInvitations'

/**
 * Minimal invitation shape required by computePostAcceptRedirect.
 * Only the fields needed for route resolution are listed here so
 * the function can be tested in isolation without constructing a
 * full OrganizationInvitation object.
 */
export interface PostAcceptInvitation {
  invite_type: InviteType
  /** Feature-level role; null for organization/admin invite types. */
  product_role: string | null
}

/**
 * Compute the destination route after a user successfully accepts an invitation
 * and is auto-logged in.
 *
 * Decision tree:
 * 1. `admin` invite → platform staff route `/admin` (RequireAdminPanel).
 * 2. `organization` invite → org owner gets full banking access, send to `/banking/dashboard`.
 * 3. `user` invite → depends on product_role:
 *    - treasury → `/projections/cash-position` (its primary workflow; treasury now
 *      has `view_banking` for the LOC Availability page, DCP-283, but the banking
 *      dashboard is not its home).
 *    - other roles (accounts_payable, view_only, admin) have `view_banking` → `/banking/dashboard`.
 *    - unknown/null product_role → safe fallback to `/projections/cash-position`.
 *
 * @param invitation - The accepted invitation containing invite_type and product_role.
 * @returns Absolute path string to navigate to after auto-login.
 */
export function computePostAcceptRedirect(invitation: PostAcceptInvitation): string {
  // Parse and validate invite_type. Use the shared schema for strict narrowing.
  const inviteTypeParsed = inviteTypeSchema.safeParse(invitation.invite_type)

  if (inviteTypeParsed.success) {
    const inviteType = inviteTypeParsed.data

    // Platform admin invite — redirect to the admin panel.
    if (inviteType === 'admin') {
      return '/admin'
    }

    // Organization owner invite — org owner always has full banking access.
    if (inviteType === 'organization') {
      return '/banking/dashboard'
    }
  }

  // User invite (or unknown type) — resolve by product_role.
  // Use the shared schema for strict narrowing; unknown/null product_role → safe fallback.
  const productRoleParsed = productRoleSchema.safeParse(invitation.product_role)

  if (productRoleParsed.success) {
    const role = productRoleParsed.data

    // Treasury's primary workflow is cash forecasting. Even though treasury now
    // has `view_banking` (granted for the LOC Availability page, DCP-283), its
    // post-accept landing stays /projections/cash-position — the banking access
    // is secondary and must not relocate treasury's home page.
    if (role === 'treasury') return '/projections/cash-position'

    const hasViewBanking = PRODUCT_ROLE_PERMISSIONS[role].includes('view_banking')

    // Roles without view_banking cannot access the /banking/* routes gated by
    // RequirePermission('view_banking'). Send them to /projections/cash-position,
    // which is a standalone route gated on the broader `view_cash_position`
    // and so remains reachable by every product role.
    return hasViewBanking ? '/banking/dashboard' : '/projections/cash-position'
  }

  // Unknown or null product_role — prefer the safe universal route over
  // a potentially blocked banking route.
  return '/projections/cash-position'
}
